import { Suspense, lazy, useState } from 'react';
import { ConfigProvider } from '@arco-design/web-react';
import type { ComponentProps } from 'react';
import ptPT from '@arco-design/web-react/es/locale/pt-PT';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppFooter } from '@/components/layout/AppFooter';
import { TabsNav } from '@/components/layout/TabsNav';
import type { TabKey } from '@/components/layout/TabsNav';
import { Watermark } from '@/components/branding/Watermark';
import { ErrorBanner } from '@/components/feedback/ErrorBanner';
import { GlobalFilters } from '@/components/filters/GlobalFilters';
import { DropZone } from '@/components/loading/DropZone';
import { FilterProvider } from '@/context/FilterContext';
import { MensalidadesProvider } from '@/context/MensalidadesContext';

type ArcoLocale = NonNullable<ComponentProps<typeof ConfigProvider>['locale']>;

// Todas as 8 views lazy. VChart (~544KB gzipped) so eh carregado quando o
// utilizador navega para uma aba que o usa — initial bundle desce de ~990KB
// para ~430KB gzipped (debaixo do target ≤500KB do PROMPT_DASHBOARD §3.8).
// User ve ViewFallback (spinner pequeno) durante ~500ms no primeiro load.
const ExecutiveOverview = lazy(() =>
  import('@/views/ExecutiveOverview').then((m) => ({ default: m.ExecutiveOverview })),
);
const TemporalAnalysis = lazy(() =>
  import('@/views/TemporalAnalysis').then((m) => ({ default: m.TemporalAnalysis })),
);
const BySupplier = lazy(() =>
  import('@/views/BySupplier').then((m) => ({ default: m.BySupplier })),
);
const ByCategory = lazy(() =>
  import('@/views/ByCategory').then((m) => ({ default: m.ByCategory })),
);
const PlatformsInventory = lazy(() =>
  import('@/views/PlatformsInventory').then((m) => ({ default: m.PlatformsInventory })),
);
const ApmAccount = lazy(() =>
  import('@/views/ApmAccount').then((m) => ({ default: m.ApmAccount })),
);
const TransactionDetail = lazy(() =>
  import('@/views/TransactionDetail').then((m) => ({ default: m.TransactionDetail })),
);
const Compliance = lazy(() =>
  import('@/views/Compliance').then((m) => ({ default: m.Compliance })),
);

function ViewFallback() {
  return (
    <div className="aiobi-view-fallback" role="status" aria-live="polite">
      <div className="aiobi-view-fallback__spinner" aria-hidden="true" />
      <span>A carregar…</span>
    </div>
  );
}

export function App() {
  const [activeTab, setActiveTab] = useState<TabKey>('executive');

  const panels: Record<TabKey, React.ReactNode> = {
    executive: (
      <Suspense fallback={<ViewFallback />}>
        <ExecutiveOverview />
      </Suspense>
    ),
    temporal: (
      <Suspense fallback={<ViewFallback />}>
        <TemporalAnalysis />
      </Suspense>
    ),
    supplier: (
      <Suspense fallback={<ViewFallback />}>
        <BySupplier />
      </Suspense>
    ),
    category: (
      <Suspense fallback={<ViewFallback />}>
        <ByCategory />
      </Suspense>
    ),
    inventory: (
      <Suspense fallback={<ViewFallback />}>
        <PlatformsInventory />
      </Suspense>
    ),
    account: (
      <Suspense fallback={<ViewFallback />}>
        <ApmAccount />
      </Suspense>
    ),
    transactions: (
      <Suspense fallback={<ViewFallback />}>
        <TransactionDetail />
      </Suspense>
    ),
    compliance: (
      <Suspense fallback={<ViewFallback />}>
        <Compliance />
      </Suspense>
    ),
  };

  return (
    <ConfigProvider locale={ptPT as unknown as ArcoLocale}>
      <MensalidadesProvider>
        <FilterProvider>
          <Watermark />
          <DropZone />
          <div className="aiobi-app">
            <AppHeader />
            <ErrorBanner />
            <GlobalFilters />
            <main className="aiobi-app__main">
              <TabsNav activeKey={activeTab} onChange={setActiveTab} panels={panels} />
            </main>
            <AppFooter />
          </div>
        </FilterProvider>
      </MensalidadesProvider>
    </ConfigProvider>
  );
}
